import dayjs from 'dayjs';
import type { ShiftRegisterItem, ShiftStatusValue } from '@/api/models';

/**
 * Pure config and helpers for the admin Shifts page: the day default, the row
 * order, the status chip and the display formats the render-only views read.
 */

/** The seller picker's "all sellers" option. Not a user id. */
export const ALL_SELLERS_KEY = '__all__';

/** Today as an ISO `YYYY-MM-DD` string in the browser's local zone. */
export const todayIso = (): string => dayjs().format('YYYY-MM-DD');

/**
 * Open shifts first, since those are the ones an admin may need to act on,
 * then the rest newest first by open time. Returns a new array.
 */
export const sortOpenFirst = (
    shifts: readonly ShiftRegisterItem[],
): ShiftRegisterItem[] =>
    [...shifts].sort((a, b) => {
        if (a.status !== b.status) {
            return a.status === 'Open' ? -1 : 1;
        }
        return dayjs(b.openedAt).valueOf() - dayjs(a.openedAt).valueOf();
    });

/** Status → label key (pages ns) + chip colour. */
export const SHIFT_STATUS_META: Record<
    ShiftStatusValue,
    { labelKey: string; color: 'success' | 'default' }
> = {
    Open: { labelKey: 'shifts.status.open', color: 'success' },
    Closed: { labelKey: 'shifts.status.closed', color: 'default' },
};

/**
 * `Intl` options for the admin's cash-count field: GEL currency, the same as
 * the seller's own close.
 */
export const CASH_COUNT_FORMAT: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'GEL',
};

/** A shift instant as `DD/MM/YYYY HH:mm` in the browser's zone; `—` when absent. */
export const formatShiftTime = (instant: string | null): string =>
    instant ? dayjs(instant).format('DD/MM/YYYY HH:mm') : '—';

/**
 * Tetri as `₾ 1,234.56`, built from integer arithmetic only so a ledger total
 * is never rounded through a float.
 */
export const formatTetri = (tetri: number): string => {
    const sign = tetri < 0 ? '−' : '';
    const abs = Math.abs(tetri);
    const lari = Math.trunc(abs / 100).toLocaleString('en-US');
    const rest = String(abs % 100).padStart(2, '0');
    return `${sign}₾ ${lari}.${rest}`;
};

/**
 * A close discrepancy's tone: a shortfall is danger, a surplus warning, an
 * exact count success. `null` while the shift is open.
 */
export const discrepancyTone = (
    discrepancy: number | null,
): 'danger' | 'warning' | 'success' | null => {
    if (discrepancy === null) {
        return null;
    }
    if (discrepancy < 0) {
        return 'danger';
    }
    return discrepancy > 0 ? 'warning' : 'success';
};
